import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '../components/ui/card';
import {
  UsersRound, Plus, Copy, Mail, Power, PowerOff, Loader2, ExternalLink,
  TrendingUp, MousePointerClick, CalendarCheck, Trophy, UserCog,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { logger } from '../lib/logger';
import { PromoteAffiliateDialog } from '../components/affiliates/PromoteAffiliateDialog';

/**
 * AffiliatesAdminPage — CRUD dashboard for the affiliate program.
 * Visible only to admin/legale/amministrativo (RBAC enforced server-side).
 *
 * Capabilities:
 *   • Create a new affiliate (name + email) → server generates a unique code
 *     and emails a magic-link Start button (uses the existing setup-account flow).
 *   • Copy the affiliate's public link to clipboard.
 *   • Toggle active/disabled.
 *   • Resend magic link if the affiliate lost the email.
 *   • Inline stats: total leads brought · booked · won.
 */
export default function AffiliatesAdminPage() {
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', email: '' });
  const [promoteTarget, setPromoteTarget] = useState(null); // ★ Fase 59 — affiliato da promuovere

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/affiliates');
      setAffiliates(r.data?.affiliates || []);
    } catch (err) {
      logger.error('list affiliates failed', err);
      toast.error('Errore nel caricamento affiliati');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const formValid = useMemo(
    () => form.name.trim().length >= 2 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()),
    [form.name, form.email],
  );

  const handleCreate = useCallback(async (e) => {
    e?.preventDefault?.();
    if (!formValid || creating) return;
    setCreating(true);
    try {
      const r = await api.post('/admin/affiliates', {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
      });
      const aff = r.data?.affiliate;
      toast.success(`Affiliato creato. Magic link inviato a ${aff?.email}`);
      setShowCreate(false);
      setForm({ name: '', email: '' });
      await fetchAll();
      // Copy the link automatically so the admin can ping the affiliate.
      if (aff?.link) {
        try {
          await navigator.clipboard.writeText(aff.link);
          toast.info('Link affiliato copiato negli appunti');
        } catch (err) {
          logger.debug('clipboard copy failed', err);
        }
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Errore creazione';
      toast.error(typeof msg === 'string' ? msg : 'Errore creazione');
    } finally {
      setCreating(false);
    }
  }, [form, formValid, creating, fetchAll]);

  const toggleActive = useCallback(async (aff) => {
    try {
      await api.patch(`/admin/affiliates/${aff.id}`, { active: !aff.active });
      toast.success(`Affiliato ${!aff.active ? 'attivato' : 'disattivato'}`);
      await fetchAll();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Errore aggiornamento';
      toast.error(msg);
    }
  }, [fetchAll]);

  const resendLink = useCallback(async (aff) => {
    try {
      await api.post(`/admin/affiliates/${aff.id}/resend-magic-link`);
      toast.success(`Magic link reinviato a ${aff.email}`);
      await fetchAll();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Errore invio link';
      toast.error(msg);
    }
  }, [fetchAll]);

  const copyLink = useCallback(async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Link copiato');
    } catch (err) {
      logger.debug('clipboard copy failed', err);
      toast.error('Copia non riuscita. Selezionalo manualmente.');
    }
  }, []);

  // Aggregate stats across all affiliates
  const totals = useMemo(() => {
    return affiliates.reduce((acc, a) => {
      const s = a.stats || {};
      return {
        affiliates: acc.affiliates + 1,
        leads:      acc.leads + (s.total || 0),
        booked:     acc.booked + (s.booked || 0),
        won:        acc.won + (s.won || 0),
      };
    }, { affiliates: 0, leads: 0, booked: 0, won: 0 });
  }, [affiliates]);

  return (
    <div className="p-6 space-y-6" data-testid="affiliates-admin-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-serif text-2xl md:text-3xl text-[hsl(var(--primary))] flex items-center gap-2">
            <UsersRound className="w-6 h-6" />
            Programma Affiliati
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Crea un affiliato, gli inviamo un magic link e gli generiamo un link personalizzato
            <code className="bg-muted px-1.5 py-0.5 rounded text-xs mx-1">/aff/&lt;codice&gt;</code>
            da condividere ovunque. Ogni lead arrivato dal suo link comparirà nella sua dashboard read-only.
          </p>
        </div>
        <Button
          onClick={() => setShowCreate(true)}
          data-testid="affiliates-create-cta"
          className="bg-[var(--gp-forest,#0F2A1B)] hover:bg-[var(--gp-forest-dark,#082019)] text-white"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Nuovo affiliato
        </Button>
      </div>

      {/* Aggregated KPI */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={<UsersRound className="w-4 h-4" />} label="Affiliati attivi" value={totals.affiliates} testid="kpi-affiliates" />
        <KpiCard icon={<TrendingUp className="w-4 h-4" />} label="Lead generati" value={totals.leads} testid="kpi-leads" />
        <KpiCard icon={<CalendarCheck className="w-4 h-4" />} label="Call prenotate" value={totals.booked} testid="kpi-booked" />
        <KpiCard icon={<Trophy className="w-4 h-4" />} label="Clienti" value={totals.won} testid="kpi-won" />
      </div>

      <Card data-testid="affiliates-table-card">
        <CardHeader>
          <CardTitle className="text-base">Affiliati registrati</CardTitle>
          <CardDescription>
            {loading ? 'Caricamento…' : `${affiliates.length} totali`}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-12 grid place-items-center text-muted-foreground">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : affiliates.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground" data-testid="affiliates-empty">
              <UsersRound className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p>Nessun affiliato. Inizia creando il primo.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Affiliato</TableHead>
                    <TableHead>Codice / Link</TableHead>
                    <TableHead className="text-center">Lead</TableHead>
                    <TableHead className="text-center">Booked</TableHead>
                    <TableHead className="text-center">Won</TableHead>
                    <TableHead className="text-center">Stato</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {affiliates.map((aff) => (
                    <TableRow key={aff.id} data-testid={`affiliate-row-${aff.code}`}>
                      <TableCell>
                        <div className="font-medium text-sm">{aff.name}</div>
                        <div className="text-xs text-muted-foreground">{aff.email}</div>
                      </TableCell>
                      <TableCell>
                        <code className="bg-muted px-1.5 py-0.5 rounded text-xs">{aff.code}</code>
                        <button
                          type="button"
                          onClick={() => copyLink(aff.link)}
                          className="ml-2 inline-flex items-center gap-1 text-xs text-[var(--gp-forest,#0F2A1B)] hover:underline"
                          data-testid={`affiliate-copy-${aff.code}`}
                        >
                          <Copy className="w-3 h-3" /> copia link
                        </button>
                      </TableCell>
                      <TableCell className="text-center font-medium">{aff.stats?.total ?? 0}</TableCell>
                      <TableCell className="text-center">{aff.stats?.booked ?? 0}</TableCell>
                      <TableCell className="text-center">
                        {(aff.stats?.won ?? 0) > 0 ? (
                          <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">{aff.stats.won}</Badge>
                        ) : 0}
                      </TableCell>
                      <TableCell className="text-center">
                        {aff.active ? (
                          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">attivo</Badge>
                        ) : (
                          <Badge variant="outline" className="bg-zinc-100 text-zinc-500">disattivo</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {aff.active && (
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => setPromoteTarget(aff)}
                            data-testid={`affiliate-promote-${aff.code}`}
                            title="Promuovi ad agente (Associato/Senior)"
                            className="text-[var(--gp-forest,#0F2A1B)]"
                          >
                            <UserCog className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => resendLink(aff)}
                          data-testid={`affiliate-resend-${aff.code}`}
                          title="Reinvia magic link"
                        >
                          <Mail className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => toggleActive(aff)}
                          data-testid={`affiliate-toggle-${aff.code}`}
                          title={aff.active ? 'Disattiva' : 'Attiva'}
                        >
                          {aff.active ? <PowerOff className="w-3.5 h-3.5" /> : <Power className="w-3.5 h-3.5" />}
                        </Button>
                        <a
                          href={aff.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-muted"
                          title="Apri link in nuova scheda"
                          data-testid={`affiliate-open-${aff.code}`}
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ★ Fase 59 — Promozione affiliato → agente CRM */}
      <PromoteAffiliateDialog
        affiliate={promoteTarget}
        open={!!promoteTarget}
        onClose={() => setPromoteTarget(null)}
        onChanged={fetchAll}
      />

      {/* Create modal */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-md" data-testid="affiliate-create-dialog">
          <DialogHeader>
            <DialogTitle>Nuovo affiliato</DialogTitle>
            <DialogDescription>
              Inserisci nome e email. Generiamo un codice univoco e gli mandiamo un magic link via email
              col bottone <b>Start</b> per attivare il suo accesso.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-3 mt-2">
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Nome e Cognome</label>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Marco Rossi"
                className="mt-1"
                required
                autoFocus
                data-testid="affiliate-form-name"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Email</label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="marco@example.com"
                className="mt-1"
                required
                data-testid="affiliate-form-email"
              />
            </div>
            <DialogFooter className="mt-4">
              <Button type="button" variant="ghost" onClick={() => setShowCreate(false)}>Annulla</Button>
              <Button
                type="submit"
                disabled={!formValid || creating}
                className="bg-[var(--gp-forest,#0F2A1B)] hover:bg-[var(--gp-forest-dark,#082019)] text-white"
                data-testid="affiliate-form-submit"
              >
                {creating ? (
                  <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Creazione…</>
                ) : 'Crea & invia magic link'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KpiCard({ icon, label, value, testid }) {
  return (
    <Card data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wider mb-1">
          {icon}
          {label}
        </div>
        <div className="text-2xl font-serif text-[hsl(var(--primary))]">{value}</div>
      </CardContent>
    </Card>
  );
}
